import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import { Toaster } from "./components/ui/sonner";
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// ============================================
// EAGER LOADED (Critical for initial render)
// ============================================
import ErrorBoundary from './components/ErrorBoundary';
import { ConfirmProvider } from './components/ConfirmProvider';
import { AuthProvider } from './kis/components/auth/AuthProvider';
import CookieConsentBanner from './pages/CookieConsentBanner';

// ============================================
// LAZY LOADED (Code-split for performance)
// ============================================

// KIS - Kausora Intelligence System Routes (lazy loaded)
const ProtectedRoute = lazy(() => import('./kis/components/auth/ProtectedRoute'));
const ProProtectedRoute = lazy(() => import('./kis/components/auth/ProProtectedRoute'));
const ProtectedAdminRoute = lazy(() => import('./admin/components/ProtectedAdminRoute'));
const KISLanding = lazy(() => import('./kis/pages/KISLanding'));
const KISLogin = lazy(() => import('./kis/pages/KISLogin'));
const KISSignup = lazy(() => import('./kis/pages/KISSignup'));
const KISAuthCallback = lazy(() => import('./kis/pages/KISAuthCallback'));
const KISForgotPassword = lazy(() => import('./kis/pages/KISForgotPassword'));
const KISResetPassword = lazy(() => import('./kis/pages/KISResetPassword'));
const KISPricingPage = lazy(() => import('./kis/pages/KISPricingPage'));
const KISBillingPage = lazy(() => import('./kis/pages/KISBillingPage'));
const KISDashboard = lazy(() => import('./kis/pages/KISDashboard'));
const KISSearchPage = lazy(() => import('./kis/pages/KISSearchPage'));
const KISResultsPage = lazy(() => import('./kis/pages/KISResultsPage'));
const KISSupplierProfile = lazy(() => import('./kis/pages/KISSupplierProfile'));
const KISReportsPage = lazy(() => import('./kis/pages/KISReportsPage'));
const KISSettingsPage = lazy(() => import('./kis/pages/KISSettingsPage'));
const KISSavedSearchesPage = lazy(() => import('./kis/pages/KISSavedSearchesPage'));
const KISSearchHistoryPage = lazy(() => import('./kis/pages/KISSearchHistoryPage'));
const KISOrganizationPage = lazy(() => import('./kis/pages/KISOrganizationPage'));
const KISAcceptInvitePage = lazy(() => import('./kis/pages/KISAcceptInvitePage'));
const KISDecisionsPage = lazy(() => import('./kis/pages/KISDecisionsPage'));
const KISIntelligencePage = lazy(() => import('./kis/pages/KISIntelligencePage'));
const KISAnalyticsPage = lazy(() => import('./kis/pages/KISAnalyticsPage'));
const KISSupplierDetailPage = lazy(() => import('./kis/pages/KISSupplierDetailPage'));
const KISPaymentSuccess = lazy(() => import('./kis/pages/KISPaymentSuccess'));
const KISMarketResearch = lazy(() => import('./kis/pages/KISMarketResearch'));
const KISCompetitiveMonitoring = lazy(() => import('./kis/pages/KISCompetitiveMonitoring'));
const KISCompetitorDetailPage = lazy(() => import('./kis/pages/KISCompetitorDetailPage'));
const KISAdminDashboard = lazy(() => import('./kis/pages/KISAdminDashboard'));
const KISAdminMarketResearch = lazy(() => import('./kis/pages/KISAdminMarketResearch'));

// Admin Dashboard Routes (lazy loaded)
const AdminDashboard = lazy(() => import('./admin/pages/AdminDashboard'));
const AdminUsers = lazy(() => import('./admin/pages/AdminUsers'));
const AdminAnalytics = lazy(() => import('./admin/pages/AdminAnalytics'));
const AdminSearches = lazy(() => import('./admin/pages/AdminSearches'));
const AdminSystem = lazy(() => import('./admin/pages/AdminSystem'));
const AdminLogs = lazy(() => import('./admin/pages/AdminLogs'));
const AdminVerification = lazy(() => import('./admin/pages/AdminVerification'));
const AdminMergeSuppliers = lazy(() => import('./admin/pages/AdminMergeSuppliers'));
const GDPRDashboard = lazy(() => import('./admin/features/gdpr/pages/GDPRDashboard'));
const CostDashboard = lazy(() => import('./admin/features/costs/pages/CostDashboard'));

// Compliance pages (shared)
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicy'));
const PrivacyDashboard = lazy(() => import('./pages/PrivacyDashboard'));

// ============================================
// LOADING FALLBACK COMPONENT
// ============================================
function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
        <p className="mt-4 text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}

// Push SPA page views to dataLayer for GTM-managed GA4
function GTMRouteListener() {
  const location = useLocation();
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'page_view',
      page: location.pathname + location.search
    });
  }, [location]);
  return null;
}

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function KISApp() {
  return (
    <ErrorBoundary
      fallbackTitle="Application Error"
      fallbackMessage="We're sorry, but something went wrong. Our team has been notified and is working to fix the issue."
    >
      <Helmet>
        <title>Kausora Intelligence System (KIS) - Turn Complexity into Confidence</title>
        <meta
          name="description"
          content="Kausora Intelligence System (KIS) is a unified AI-first platform that transforms the web and enterprise data into structured intelligence. Discover suppliers, analyze markets, and make smarter, faster decisions."
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: `{
              "@context": "https://schema.org",
              "@type": "SoftwareApplication",
              "name": "Kausora Intelligence System",
              "alternateName": "KIS",
              "applicationCategory": "BusinessApplication",
              "operatingSystem": "Web",
              "offers": {
                "@type": "Offer",
                "price": "79",
                "priceCurrency": "USD"
              },
              "provider": {
                "@type": "Organization",
                "name": "Kausora Technologies",
                "url": "https://www.kausora.com"
              }
            }`
          }}
        />
      </Helmet>

      <Router>
        <GTMRouteListener />
        <AuthProvider>
          <QueryClientProvider client={queryClient}>
            <CookieConsentBanner />
            <Analytics />
            <Toaster richColors position="top-right" />

            <ConfirmProvider>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                {/* KIS Routes - Clean URLs without /kis prefix */}
                <Route path="/" element={<KISLanding />} />
                <Route path="/login" element={<KISLogin />} />
                <Route path="/signup" element={<KISSignup />} />
                <Route path="/auth/callback" element={<KISAuthCallback />} />
                <Route path="/forgot-password" element={<KISForgotPassword />} />
                <Route path="/reset-password" element={<KISResetPassword />} />
                <Route path="/pricing" element={<KISPricingPage />} />
                {/* Invitation acceptance (public; the page handles logged-out users) */}
                <Route path="/invite/:token" element={<KISAcceptInvitePage />} />

                {/* Legacy /kis/* routes - Redirect to clean URLs for backward compatibility */}
                <Route path="/kis" element={<KISLanding />} />
                <Route path="/kis/login" element={<KISLogin />} />
                <Route path="/kis/invite/:token" element={<KISAcceptInvitePage />} />
                <Route path="/kis/signup" element={<KISSignup />} />
                <Route path="/kis/auth/callback" element={<KISAuthCallback />} />
                <Route path="/kis/forgot-password" element={<KISForgotPassword />} />
                <Route path="/kis/reset-password" element={<KISResetPassword />} />
                <Route path="/kis/pricing" element={<KISPricingPage />} />
                <Route
                  path="/billing"
                  element={
                    <ProtectedRoute>
                      <KISBillingPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <ErrorBoundary
                        fallbackTitle="Dashboard Error"
                        fallbackMessage="We're having trouble loading your dashboard. Please try refreshing the page."
                      >
                        <KISDashboard />
                      </ErrorBoundary>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/search"
                  element={
                    <ProtectedRoute>
                      <KISSearchPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/results/:queryId"
                  element={
                    <ProtectedRoute>
                      <KISResultsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/suppliers/:supplierId"
                  element={
                    <ProtectedRoute>
                      <KISSupplierProfile />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <ProProtectedRoute featureName="Intelligence Reports">
                      <KISReportsPage />
                    </ProProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <KISSettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/saved-searches"
                  element={
                    <ProtectedRoute>
                      <KISSavedSearchesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/organization"
                  element={
                    <ProtectedRoute>
                      <KISOrganizationPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/decisions"
                  element={
                    <ProtectedRoute>
                      <KISDecisionsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/kis/decisions"
                  element={
                    <ProtectedRoute>
                      <KISDecisionsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/intelligence"
                  element={
                    <ProtectedRoute>
                      <KISIntelligencePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/kis/intelligence"
                  element={
                    <ProtectedRoute>
                      <KISIntelligencePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/search-history"
                  element={
                    <ProtectedRoute>
                      <KISSearchHistoryPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analytics"
                  element={
                    <ProProtectedRoute featureName="Advanced Analytics">
                      <KISAnalyticsPage />
                    </ProProtectedRoute>
                  }
                />
                <Route
                  path="/market-research"
                  element={
                    <ProProtectedRoute featureName="Market Research">
                      <KISMarketResearch />
                    </ProProtectedRoute>
                  }
                />
                <Route
                  path="/competitive-monitoring"
                  element={
                    <ProProtectedRoute featureName="Competitive Monitoring">
                      <KISCompetitiveMonitoring />
                    </ProProtectedRoute>
                  }
                />
                <Route
                  path="/competitors/:competitorId"
                  element={
                    <ProProtectedRoute featureName="Competitive Monitoring">
                      <KISCompetitorDetailPage />
                    </ProProtectedRoute>
                  }
                />
                <Route
                  path="/suppliers"
                  element={
                    <ProtectedRoute>
                      <KISSupplierDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/payment-success"
                  element={
                    <ProtectedRoute>
                      <KISPaymentSuccess />
                    </ProtectedRoute>
                  }
                />

                {/* Legacy /kis/* protected routes - For backward compatibility */}
                <Route
                  path="/kis/billing"
                  element={
                    <ProtectedRoute>
                      <KISBillingPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/kis/dashboard"
                  element={
                    <ProtectedRoute>
                      <ErrorBoundary
                        fallbackTitle="Dashboard Error"
                        fallbackMessage="We're having trouble loading your dashboard. Please try refreshing the page."
                      >
                        <KISDashboard />
                      </ErrorBoundary>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/kis/search"
                  element={
                    <ProtectedRoute>
                      <KISSearchPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/kis/results/:queryId"
                  element={
                    <ProtectedRoute>
                      <KISResultsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/kis/suppliers/:supplierId"
                  element={
                    <ProtectedRoute>
                      <KISSupplierProfile />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/kis/reports"
                  element={
                    <ProProtectedRoute featureName="Intelligence Reports">
                      <KISReportsPage />
                    </ProProtectedRoute>
                  }
                />
                <Route
                  path="/kis/settings"
                  element={
                    <ProtectedRoute>
                      <KISSettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/kis/saved-searches"
                  element={
                    <ProtectedRoute>
                      <KISSavedSearchesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/kis/search-history"
                  element={
                    <ProtectedRoute>
                      <KISSearchHistoryPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/kis/analytics"
                  element={
                    <ProProtectedRoute featureName="Advanced Analytics">
                      <KISAnalyticsPage />
                    </ProProtectedRoute>
                  }
                />
                <Route
                  path="/kis/market-research"
                  element={
                    <ProProtectedRoute featureName="Market Research">
                      <KISMarketResearch />
                    </ProProtectedRoute>
                  }
                />
                <Route
                  path="/kis/competitive-monitoring"
                  element={
                    <ProProtectedRoute featureName="Competitive Monitoring">
                      <KISCompetitiveMonitoring />
                    </ProProtectedRoute>
                  }
                />
                <Route
                  path="/kis/competitors/:competitorId"
                  element={
                    <ProProtectedRoute featureName="Competitive Monitoring">
                      <KISCompetitorDetailPage />
                    </ProProtectedRoute>
                  }
                />
                <Route
                  path="/kis/suppliers"
                  element={
                    <ProtectedRoute>
                      <KISSupplierDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/kis/payment-success"
                  element={
                    <ProtectedRoute>
                      <KISPaymentSuccess />
                    </ProtectedRoute>
                  }
                />

                {/* Admin Routes */}
                <Route
                  path="/admin"
                  element={
                    <ProtectedAdminRoute>
                      <AdminDashboard />
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path="/admin/dashboard"
                  element={
                    <ProtectedAdminRoute>
                      <AdminDashboard />
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <ProtectedAdminRoute>
                      <AdminUsers />
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path="/admin/analytics"
                  element={
                    <ProtectedAdminRoute>
                      <AdminAnalytics />
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path="/admin/searches"
                  element={
                    <ProtectedAdminRoute>
                      <AdminSearches />
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path="/admin/system"
                  element={
                    <ProtectedAdminRoute>
                      <AdminSystem />
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path="/admin/logs"
                  element={
                    <ProtectedAdminRoute>
                      <AdminLogs />
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path="/admin/verification"
                  element={
                    <ProtectedAdminRoute>
                      <AdminVerification />
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path="/admin/merge-suppliers"
                  element={
                    <ProtectedAdminRoute>
                      <AdminMergeSuppliers />
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path="/admin/gdpr"
                  element={
                    <ProtectedAdminRoute>
                      <GDPRDashboard />
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path="/admin/costs"
                  element={
                    <ProtectedAdminRoute>
                      <CostDashboard />
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path="/admin/kis-dashboard"
                  element={
                    <ProtectedAdminRoute>
                      <KISAdminDashboard />
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path="/admin/market-research"
                  element={
                    <ProtectedAdminRoute>
                      <KISAdminMarketResearch />
                    </ProtectedAdminRoute>
                  }
                />

                {/* Legacy /kis/admin/* routes - For backward compatibility */}
                <Route
                  path="/kis/admin"
                  element={
                    <ProtectedAdminRoute>
                      <AdminDashboard />
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path="/kis/admin/dashboard"
                  element={
                    <ProtectedAdminRoute>
                      <AdminDashboard />
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path="/kis/admin/users"
                  element={
                    <ProtectedAdminRoute>
                      <AdminUsers />
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path="/kis/admin/analytics"
                  element={
                    <ProtectedAdminRoute>
                      <AdminAnalytics />
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path="/kis/admin/searches"
                  element={
                    <ProtectedAdminRoute>
                      <AdminSearches />
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path="/kis/admin/system"
                  element={
                    <ProtectedAdminRoute>
                      <AdminSystem />
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path="/kis/admin/logs"
                  element={
                    <ProtectedAdminRoute>
                      <AdminLogs />
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path="/kis/admin/verification"
                  element={
                    <ProtectedAdminRoute>
                      <AdminVerification />
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path="/kis/admin/merge-suppliers"
                  element={
                    <ProtectedAdminRoute>
                      <AdminMergeSuppliers />
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path="/kis/admin/gdpr"
                  element={
                    <ProtectedAdminRoute>
                      <GDPRDashboard />
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path="/kis/admin/costs"
                  element={
                    <ProtectedAdminRoute>
                      <CostDashboard />
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path="/kis/admin/kis-dashboard"
                  element={
                    <ProtectedAdminRoute>
                      <KISAdminDashboard />
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path="/kis/admin/market-research"
                  element={
                    <ProtectedAdminRoute>
                      <KISAdminMarketResearch />
                    </ProtectedAdminRoute>
                  }
                />

                {/* Shared compliance pages */}
                <Route
                  path="/privacy-policy"
                  element={<PrivacyPolicyPage />}
                />
                <Route
                  path="/privacy-dashboard"
                  element={
                    <ProtectedRoute>
                      <PrivacyDashboard />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Suspense>
            </ConfirmProvider>
          </QueryClientProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default KISApp;
